import React, { useState } from 'react';
import { PlusIcon, XIcon, EditIcon, TrashIcon, ClockIcon, FilterIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { NotificationRule, NotificationEvent, NotificationChannel } from './NotificationsManagement';
import { StatusBadge } from '../../StatusBadge';
interface NotificationRulesProps {
  rules: NotificationRule[];
  setRules: React.Dispatch<React.SetStateAction<NotificationRule[]>>;
  events: NotificationEvent[];
  channels: NotificationChannel[];
  locale: string;
}
export function NotificationRules({
  rules,
  setRules,
  events,
  channels,
  locale
}: NotificationRulesProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<NotificationRule | null>(null);
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([]);
  const [frequency, setFrequency] = useState<'immediate' | 'daily' | 'weekly'>('immediate');
  const getEventById = (eventId: string) => {
    return events.find(event => event.id === eventId);
  };
  const getChannelById = (channelId: string) => {
    return channels.find(channel => channel.id === channelId);
  };
  const getFrequencyLabel = (freq: string) => {
    switch (freq) {
      case 'immediate':
        return 'Immediate';
      case 'daily':
        return 'Daily Digest';
      case 'weekly':
        return 'Weekly Digest';
      default:
        return freq;
    }
  };
  const handleToggleRule = (ruleId: string) => {
    setRules(rules.map(rule => rule.id === ruleId ? {
      ...rule,
      isEnabled: !rule.isEnabled
    } : rule));
  };
  const handleEditRule = (rule: NotificationRule) => {
    setEditingRule(rule);
    setSelectedEventIds([rule.eventId]);
    setSelectedChannelIds(rule.channels);
    setFrequency(rule.frequency);
    setIsDrawerOpen(true);
  };
  const handleDeleteRule = (ruleId: string) => {
    setRules(rules.filter(rule => rule.id !== ruleId));
  };
  const handleAddRule = () => {
    setEditingRule(null);
    setSelectedEventIds([]);
    setSelectedChannelIds([]);
    setFrequency('immediate');
    setIsDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingRule(null);
  };
  const handleSaveRule = () => {
    if (selectedEventIds.length === 0 || selectedChannelIds.length === 0) {
      // Show validation error
      return;
    }
    // Create a new rule for each selected event
    const newRules = selectedEventIds.map(eventId => {
      const event = getEventById(eventId);
      if (!event) return null;
      const ruleData = {
        id: editingRule?.id || `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        eventId,
        name: `${event.name} Notifications`,
        description: `Send notifications for ${event.name.toLowerCase()}`,
        channels: selectedChannelIds,
        frequency,
        isEnabled: true,
        filters: {},
        scopeLevel: 'Tenant' as const,
        createdAt: editingRule?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      return ruleData;
    }).filter(Boolean) as NotificationRule[];
    if (editingRule) {
      // Update existing rule
      setRules(rules.map(rule => rule.id === editingRule.id ? newRules[0] : rule));
    } else {
      // Add new rules
      setRules([...rules, ...newRules]);
    }
    setIsDrawerOpen(false);
  };
  return <div data-id="element-17303">
      <div className="mb-6 flex justify-between items-center" data-id="element-17304">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17305">
          Notification Rules & Events
        </h3>
        <button type="button" onClick={handleAddRule} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17306">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-17307" />
          Add Rule
        </button>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-17308">
        <ul className="divide-y divide-gray-200" data-id="element-17309">
          {rules.length === 0 ? <li className="px-6 py-10 text-center" data-id="element-17310">
              <p className="text-sm text-gray-500" data-id="element-17311">
                No notification rules configured yet.
              </p>
              <button type="button" onClick={handleAddRule} className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17312">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-17313" />
                Add Your First Rule
              </button>
            </li> : rules.map(rule => {
          const event = getEventById(rule.eventId);
          return <li key={rule.id} data-id="element-17314">
                  <div className="px-4 py-4 sm:px-6" data-id="element-17315">
                    <div className="flex items-center justify-between" data-id="element-17316">
                      <div className="flex items-center" data-id="element-17317">
                        <button onClick={() => handleToggleRule(rule.id)} className={`mr-3 h-5 w-5 rounded-full flex items-center justify-center ${rule.isEnabled ? 'text-green-500 bg-green-50' : 'text-gray-400 bg-gray-100'}`} data-id="element-17318">
                          {rule.isEnabled ? <CheckCircleIcon className="h-5 w-5" data-id="element-17319" /> : <XCircleIcon className="h-5 w-5" data-id="element-17320" />}
                        </button>
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-17321">
                          {rule.name}
                        </p>
                      </div>
                      <div className="ml-2 flex-shrink-0 flex" data-id="element-17322">
                        <StatusBadge status={rule.isEnabled ? 'success' : 'error'} text={rule.isEnabled ? 'Enabled' : 'Disabled'} data-id="element-17323" />
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between" data-id="element-17324">
                      <div className="sm:flex" data-id="element-17325">
                        <p className="flex items-center text-sm text-gray-500" data-id="element-17326">
                          Event: {event?.name || 'Unknown Event'}
                        </p>
                        <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6" data-id="element-17327">
                          <ClockIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-17328" />
                          {getFrequencyLabel(rule.frequency)}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-17329">
                        <p data-id="element-17330">
                          Channels:{' '}
                          {rule.channels.map(channelId => getChannelById(channelId)?.name || 'Unknown').join(', ')}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex justify-end space-x-2" data-id="element-17331">
                      <button type="button" onClick={() => handleEditRule(rule)} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17332">
                        <EditIcon className="h-4 w-4" aria-hidden="true" data-id="element-17333" />
                      </button>
                      <button type="button" onClick={() => handleDeleteRule(rule.id)} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-17334">
                        <TrashIcon className="h-4 w-4" aria-hidden="true" data-id="element-17335" />
                      </button>
                    </div>
                  </div>
                </li>;
        })}
        </ul>
      </div>
      {/* Rule Configuration Drawer */}
      {isDrawerOpen && <div className="fixed inset-0 overflow-hidden z-50" data-id="element-17336">
          <div className="absolute inset-0 overflow-hidden" data-id="element-17337">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDrawer} data-id="element-17338"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-17339">
              <div className="relative w-screen max-w-md" data-id="element-17340">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll" data-id="element-17341">
                  <div className="px-4 py-6 sm:px-6" data-id="element-17342">
                    <div className="flex items-start justify-between" data-id="element-17343">
                      <h2 className="text-lg font-medium text-gray-900" data-id="element-17344">
                        {editingRule ? 'Edit Rule' : 'Create New Rule'}
                      </h2>
                      <div className="ml-3 h-7 flex items-center" data-id="element-17345">
                        <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-17346">
                          <span className="sr-only" data-id="element-17347">Close panel</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-17348" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 py-6 overflow-y-auto px-4 sm:px-6" data-id="element-17349">
                    <div className="space-y-6" data-id="element-17350">
                      {/* Event Selection */}
                      <div data-id="element-17351">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-17352">
                          Select Events
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-17353">
                          {events.map(event => <div key={event.id} className="relative flex items-start" data-id="element-17354">
                              <div className="flex items-center h-5" data-id="element-17355">
                                <input id={`event-${event.id}`} type="checkbox" checked={selectedEventIds.includes(event.id)} onChange={e => {
                            if (e.target.checked) {
                              setSelectedEventIds([...selectedEventIds, event.id]);
                            } else {
                              setSelectedEventIds(selectedEventIds.filter(id => id !== event.id));
                            }
                          }} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-17356" />
                              </div>
                              <div className="ml-3 text-sm" data-id="element-17357">
                                <label htmlFor={`event-${event.id}`} className="font-medium text-gray-700" data-id="element-17358">
                                  {event.name}
                                </label>
                                <p className="text-gray-500" data-id="element-17359">
                                  {event.description}
                                </p>
                              </div>
                            </div>)}
                        </div>
                      </div>
                      {/* Channel Selection */}
                      <div data-id="element-17360">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-17361">
                          Delivery Channels
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-17362">
                          {channels.filter(channel => channel.isConfigured).map(channel => <div key={channel.id} className="relative flex items-start" data-id="element-17363">
                                <div className="flex items-center h-5" data-id="element-17364">
                                  <input id={`channel-${channel.id}`} type="checkbox" checked={selectedChannelIds.includes(channel.id)} onChange={e => {
                            if (e.target.checked) {
                              setSelectedChannelIds([...selectedChannelIds, channel.id]);
                            } else {
                              setSelectedChannelIds(selectedChannelIds.filter(id => id !== channel.id));
                            }
                          }} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-17365" />
                                </div>
                                <div className="ml-3 text-sm" data-id="element-17366">
                                  <label htmlFor={`channel-${channel.id}`} className="font-medium text-gray-700" data-id="element-17367">
                                    {channel.name}
                                  </label>
                                </div>
                              </div>)}
                        </div>
                      </div>
                      {/* Frequency Selection */}
                      <div data-id="element-17368">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-17369">
                          Notification Frequency
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-17370">
                          <div className="relative flex items-start" data-id="element-17371">
                            <div className="flex items-center h-5" data-id="element-17372">
                              <input id="frequency-immediate" type="radio" checked={frequency === 'immediate'} onChange={() => setFrequency('immediate')} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-17373" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-17374">
                              <label htmlFor="frequency-immediate" className="font-medium text-gray-700" data-id="element-17375">
                                Immediate
                              </label>
                              <p className="text-gray-500" data-id="element-17376">
                                Send notifications as events occur
                              </p>
                            </div>
                          </div>
                          <div className="relative flex items-start" data-id="element-17377">
                            <div className="flex items-center h-5" data-id="element-17378">
                              <input id="frequency-daily" type="radio" checked={frequency === 'daily'} onChange={() => setFrequency('daily')} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-17379" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-17380">
                              <label htmlFor="frequency-daily" className="font-medium text-gray-700" data-id="element-17381">
                                Daily Digest
                              </label>
                              <p className="text-gray-500" data-id="element-17382">
                                Group notifications and send once per day
                              </p>
                            </div>
                          </div>
                          <div className="relative flex items-start" data-id="element-17383">
                            <div className="flex items-center h-5" data-id="element-17384">
                              <input id="frequency-weekly" type="radio" checked={frequency === 'weekly'} onChange={() => setFrequency('weekly')} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-17385" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-17386">
                              <label htmlFor="frequency-weekly" className="font-medium text-gray-700" data-id="element-17387">
                                Weekly Digest
                              </label>
                              <p className="text-gray-500" data-id="element-17388">
                                Group notifications and send once per week
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Optional Filters */}
                      <div data-id="element-17389">
                        <div className="flex items-center justify-between" data-id="element-17390">
                          <label className="block text-sm font-medium text-gray-700" data-id="element-17391">
                            Additional Filters (Optional)
                          </label>
                          <button type="button" className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17392">
                            <FilterIcon className="h-4 w-4 mr-1" data-id="element-17393" />
                            Add Filter
                          </button>
                        </div>
                        <div className="mt-2" data-id="element-17394">
                          <p className="text-sm text-gray-500" data-id="element-17395">
                            No filters applied. Notifications will be sent for
                            all matching events.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 px-4 py-4 flex justify-end border-t border-gray-200" data-id="element-17396">
                    <button type="button" className="mr-3 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-17397">
                      Cancel
                    </button>
                    <button type="button" className="bg-indigo-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleSaveRule} data-id="element-17398">
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}